import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { reserveFounderSpot } from "@/lib/growth.functions";

export function FoundersReserve({ compact = false }: { compact?: boolean }) {
  const reserve = useServerFn(reserveFounderSpot);
  const [form, setForm] = useState({ email: "", displayName: "", handle: "", showPublicly: true });
  const [status, setStatus] = useState<"idle" | "sending" | "done" | "error">("idle");
  const [message, setMessage] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setStatus("sending");
    setMessage(null);
    try {
      const res = await reserve({
        data: {
          email: form.email,
          displayName: form.displayName,
          handle: form.handle || null,
          showPublicly: form.showPublicly,
        },
      });
      setStatus(res.ok ? "done" : "error");
      setMessage(res.message);
    } catch {
      setStatus("error");
      setMessage("Something went wrong. Try again in a moment.");
    }
  }

  if (status === "done") {
    return (
      <div className={compact ? "soft-panel p-6" : "vault-panel p-7"}>
        <p className="micro-label">Spot held</p>
        <p className="mt-4 text-sm leading-relaxed text-muted-foreground">{message}</p>
      </div>
    );
  }

  const field =
    "w-full rounded-sm border border-border bg-background px-4 py-3 text-sm outline-none transition-colors focus:border-foreground/40";

  return (
    <form onSubmit={submit} className={compact ? "soft-panel p-6" : "vault-panel p-7"}>
      <p className="micro-label">Claim a founder spot</p>
      <div className="mt-5 space-y-3">
        <input
          className={field}
          type="email"
          required
          aria-label="Email address"
          placeholder="you@company.com"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
        />
        <input
          className={field}
          required
          aria-label="Name for the wall"
          placeholder="Name as it should appear"
          value={form.displayName}
          onChange={(e) => setForm({ ...form, displayName: e.target.value })}
        />
        <input
          className={field}
          aria-label="Handle or company (optional)"
          placeholder="Handle or company (optional)"
          value={form.handle}
          onChange={(e) => setForm({ ...form, handle: e.target.value })}
        />
        <label className="flex items-start gap-3 text-sm text-muted-foreground">
          <input
            type="checkbox"
            className="mt-1 h-3.5 w-3.5 accent-foreground"
            checked={form.showPublicly}
            onChange={(e) => setForm({ ...form, showPublicly: e.target.checked })}
          />
          Inscribe my name on the founders wall. Uncheck to stay anonymous — the spot is the same.
        </label>
      </div>
      <button
        type="submit"
        disabled={status === "sending"}
        className="sheen mt-6 inline-flex w-full items-center justify-center rounded-sm bg-primary px-6 py-3.5 text-sm font-semibold text-primary-foreground transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60"
      >
        {status === "sending" ? "Holding your spot…" : "Hold my founder spot"}
      </button>
      {message && <p className="mt-3 text-sm text-muted-foreground">{message}</p>}
      <p className="mt-3 text-xs text-muted-foreground/70">
        No card charged today. We contact you the moment the founding round opens.
      </p>
    </form>
  );
}
